import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Pagination } from "nlib/ui";
import { checkMatchesFetching, getMatchesData, getSelectedBusinessId } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import { useMainApi } from "hooks";
import BusinessesActions from "actions/BusinessesActions";
import Constants from "const/Constants";
import DataStatuses from "const/DataStatuses";
import DocumentsTableRow from "./lib/DocumentsTableRow";
import Filters from "./lib/Filters";
import MainApiRoutes from "const/MainApiRoutes";
import NoDataContent from "nlib/common/NoDataContent";
import Preloader from "nlib/common/Preloader";
import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import RestApi from "api/RestApi";
import Table, { TableHead, TableRow } from "nlib/ui/Table";

const { BUSINESSES, DOCUMENTS, STATS } = MainApiRoutes;

const STATUSES = [DataStatuses.EXPORTED, DataStatuses.TO_REVIEW];

const SelectDocumentTab = ({ transaction, onSelect }) => {
  const { id: transactionId, type: transactionType } = transaction;

  const dispatch = useDispatch();

  const { uiTexts } = useSelector(getTextsData);

  const fetchingMatches = useSelector(checkMatchesFetching);

  const selectedBusinessId = useSelector(getSelectedBusinessId);

  const matches = useSelector(getMatchesData);

  const [filters, setFilters] = useState({});

  const [sortingProps, setSortingProps] = useState({ id: "paymentType", desc: true });

  const [page, setPage] = useState(1);

  const [{ results: documentsData = [] }, fetchingDocuments] = useMainApi({
    method: RestApi.REQUEST_METHODS.POST,
    initialData: { results: [] },
    parameters: [
      `${BUSINESSES}/${selectedBusinessId}${DOCUMENTS}`,
      null,
      {
        ...filters,
        text: filters.text || null,
        status: STATUSES,
        offset: (page - 1) * Constants.TABLE_PAGE_SIZE,
        limit: Constants.TABLE_PAGE_SIZE,
        sortings: sortingProps.id ? sortingProps : null,
        transactionType,
        paid: false
      }
    ],
    dependencies: [filters, page, sortingProps]
  });

  const [filteredDocumentsStats, fetchingFilteredDocumentsStats] = useMainApi({
    method: RestApi.REQUEST_METHODS.GET,
    initialData: {},
    parameters: [
      `${BUSINESSES}/${selectedBusinessId}${DOCUMENTS}${STATS}`,
      { ...filters, text: filters.text || null, transactionType }
    ],
    dependencies: [filters]
  });

  const filteredDocumentsCount = useMemo(() => {
    return STATUSES.reduce((result, status) => {
      return result + (filteredDocumentsStats[status] || { count: 0 }).count;
    }, 0);
  }, [filteredDocumentsStats]);

  const handleChangeFilters = useCallback((value) => {
    setFilters((prev) => ({ ...prev, ...value }));
    setPage(1);
  }, []);

  const handleTableSortChange = useCallback((value) => {
    setSortingProps({ id: value.sortBy, desc: value.sortOrder === "desc" });
  }, []);

  const handlePageChange = useCallback((value) => {
    setPage(value);
  }, []);

  const fetchingData = fetchingMatches || fetchingDocuments || fetchingFilteredDocumentsStats;

  useLayoutEffect(() => {
    dispatch(BusinessesActions.fetchDataMatchesList(transactionId));
  }, [transactionId, dispatch]);

  return (
    <div className={Css.selectDocumentTab}>
      <Filters
        filters={filters}
        onChange={handleChangeFilters} />
      <Table
        theme="flat"
        sortBy={sortingProps.id}
        sortOrder={sortingProps.desc ? "desc" : "asc"}
        onSortChange={handleTableSortChange}
        className={Css.table}>
        <TableRow>
          <TableHead accessor="status" className={Css.statusCell}>{uiTexts.status}</TableHead>
          <TableHead accessor="type" className={Css.typeCell}>{uiTexts.type}</TableHead>
          <TableHead accessor="originalDocumentId" className={Css.documentIdCell}>{`${uiTexts.document} #`}</TableHead>
          <TableHead accessor="amount" className={Css.amountCell}>{uiTexts.total}</TableHead>
          <TableHead accessor="address" className={Css.addressCell}>{uiTexts.payee}</TableHead>
          <TableHead accessor="issueDate" className={Css.timestampCell}>{uiTexts.issueDate}</TableHead>
          <TableHead accessor="description" className={Css.descriptionCell}>{uiTexts.description}</TableHead>
          <TableHead className={Css.actionsCell}>{uiTexts.actions}</TableHead>
        </TableRow>
        {!!matches.length && (
          <>
            <div className={Css.title}>{uiTexts.matchingDocuments}</div>
            {matches.map(({ document: documentData }) => (
              <DocumentsTableRow
                key={documentData.id}
                transactionType={transactionType}
                transactionAmount={transaction.amount}
                documentData={documentData}
                onSelect={onSelect} />
            ))}
          </>
        )}
        {documentsData.length
          ? (
            <>
              {!!matches.length && <div className={Css.title}>{uiTexts.otherDocuments}</div>}
              {documentsData.map((documentData) => (
                <DocumentsTableRow
                  key={documentData.id}
                  transactionType={transactionType}
                  transactionAmount={transaction.amount}
                  documentData={documentData}
                  onSelect={onSelect} />
              ))}
            </>
          )
          : (
            fetchingData ? <Preloader className={Css.preloader} /> : (
              <NoDataContent
                icon={Icons.CreditCard}
                title={uiTexts.noDocumentsFound} />
            )
          )}

      </Table>
      {(filteredDocumentsCount > Constants.TABLE_PAGE_SIZE) && (
        <Pagination
          className={Css.pagination}
          count={filteredDocumentsCount}
          page={page}
          pageSize={Constants.TABLE_PAGE_SIZE}
          disabled={fetchingData}
          onChange={handlePageChange} />
      )}
    </div>
  );
};

export default React.memo(SelectDocumentTab);
